import React, { useState, Children, Fragment, Suspense, Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StarIcon, ChevronDownIcon, FlameIcon, MoonIcon, BookOpenIcon, ShoppingCartIcon, GiftIcon, CheckCircleIcon, ClockIcon, CalendarIcon, Share2Icon, HeadphonesIcon, BookIcon, TagIcon, AlertCircleIcon, AlertTriangleIcon, XIcon, BookmarkIcon, BookmarkPlusIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BackButton from '../components/UI/BackButton';
import SaveToFavorites from '../components/UI/SaveToFavorites';
import SaveToTracker from '../components/UI/SaveToTracker';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
import { usePlatform } from '../context/PlatformContext';
import { aftertideSeries, aftertideBooks } from '../data/aftertideBooksData';
import { spiceLevelMap, darknessLevelMap } from './book-detail/constants';
// --- Mock Data ---
const seriesData = {
  id: 1,
  name: 'Echo Series',
  author: 'Elena Richards',
  authorId: 'elena-richards',
  authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
  authorBio: 'Elena Richards is a NYT bestselling author of romantic suspense and thrillers. She lives in the Pacific Northwest with her two dogs and an overflowing bookshelf.',
  description: 'A gripping series following investigative journalist Mira Hayes as she uncovers a conspiracy that could change everything. From the first shocking discovery to the final revelation, this series takes readers on a thrilling journey through deceit, danger, and unexpected alliances.',
  averageRating: 4.8,
  totalReviews: 1245,
  averageSpice: 2,
  averageDarkness: 4,
  releasedCount: 3,
  totalBooks: 4,
  status: 'Ongoing',
  nextBookDate: 'October 15, 2024',
  genres: ['Mystery', 'Thriller', 'Suspense'],
  tropes: ['Cold Case', 'Small Town Secrets', 'Conspiracy', 'Political Intrigue', 'Betrayal', 'Government Corruption'],
  themes: ['Justice', 'Redemption', 'Truth vs. Power'],
  contentWarnings: ['Violence', 'Death', 'Political Corruption', 'Trauma'],
  sharedTags: ['Female Protagonist', 'Investigative Journalism', 'Series'],
  bundleFormats: [{
    type: 'eBook',
    price: 19.99,
    available: true
  }, {
    type: 'Paperback',
    price: 49.99,
    available: true
  }, {
    type: 'Audiobook',
    price: 64.99,
    available: true
  }],
  retailers: [{
    name: 'Amazon',
    url: '#'
  }, {
    name: 'Barnes & Noble',
    url: '#'
  }, {
    name: 'Kobo',
    url: '#'
  }, {
    name: 'Apple Books',
    url: '#'
  }],
  narrators: [{
    name: 'Sarah Johnson',
    id: 'sarah-johnson'
  }, {
    name: 'Mark Davis',
    id: 'mark-davis'
  }],
  donationInfo: {
    hasDonation: true,
    percentage: 5,
    nonprofitName: "Children's Literacy Foundation",
    nonprofitSlug: 'childrens-literacy-foundation'
  },
  books: [{
    id: 101,
    title: 'The Silent Echo',
    position: 1,
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.7,
    spiceLevel: 2,
    darknessLevel: 3,
    pages: 342,
    releaseDate: '2021',
    description: "When investigative journalist Mira Hayes returns to her hometown after fifteen years, she's confronted with the unsolved disappearance that drove her away. As Mira digs deeper into the case, she discovers that the town harbors dark secrets—and someone is determined to keep them buried."
  }, {
    id: 102,
    title: 'Echoes of Darkness',
    position: 2,
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.8,
    spiceLevel: 2,
    darknessLevel: 4,
    pages: 368,
    releaseDate: '2022',
    description: "With the truth about her hometown partially revealed, Mira Hayes follows the trail of corruption to the state capital. As threats escalate and allies become enemies, Mira must decide how much she's willing to risk to expose a conspiracy that goes deeper than she ever imagined."
  }, {
    id: 103,
    title: 'The Final Echo',
    position: 3,
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.9,
    spiceLevel: 2,
    darknessLevel: 5,
    pages: 412,
    releaseDate: '2023',
    description: 'In the thrilling conclusion to the Echo Series, Mira Hayes faces her most dangerous challenge yet as she prepares to expose the truth to the world. With enemies closing in from all sides and shocking revelations about those closest to her, Mira must find the courage to finish what she started.'
  }, {
    id: 104,
    title: 'Echoes Remastered',
    position: 4,
    coverImage: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800&q=80',
    rating: 0,
    spiceLevel: 0,
    darknessLevel: 0,
    pages: 0,
    releaseDate: 'Oct 2024',
    isPreorder: true,
    description: 'The highly anticipated prequel novella revealing the origins of the conspiracy.'
  }],
  reviews: [{
    id: 1,
    user: 'Sarah J.',
    rating: 5,
    text: 'This series kept me on the edge of my seat from start to finish! The character development is incredible.'
  }, {
    id: 2,
    user: 'Mike T.',
    rating: 5,
    text: 'Best thriller series I have read in years. Highly recommend for fans of complex plots.'
  }, {
    id: 3,
    user: 'BookLover99',
    rating: 4,
    text: 'A bit slow in the second book but the payoff in the third is absolutely worth it.'
  }]
};
const allSeriesData: Record<string, typeof seriesData> = {
  '1': seriesData
};
type ReadStatus = 'read' | 'reading' | 'tbr' | 'dnf';
const STATUS_OPTIONS: {
  value: ReadStatus;
  label: string;
}[] = [{
  value: 'tbr',
  label: 'Want to Read'
}, {
  value: 'reading',
  label: 'Currently Reading'
}, {
  value: 'read',
  label: 'Read'
}, {
  value: 'dnf',
  label: 'Did Not Finish'
}];
const SeriesDetailPage = () => {
  const {
    id
  } = useParams();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  const {
    isAfterTide
  } = usePlatform();
  let series = allSeriesData[id || '1'] || seriesData;
  if (isAfterTide) {
    const atSeries = aftertideSeries.find(s => s.id === id);
    if (atSeries) {
      const atBooks = atSeries.bookIds.map(bookId => aftertideBooks.find(b => b.id === bookId)).filter((b): b is NonNullable<typeof b> => b !== undefined);
      const avgDarkness = atBooks.length ? atBooks.reduce((sum, b) => sum + (b.darknessTier || 0), 0) / atBooks.length : 0;
      const avgSpice = atBooks.length ? atBooks.reduce((sum, b) => sum + (b.spiceLevel || 0), 0) / atBooks.length : 0;
      const allGenres = Array.from(new Set(atBooks.flatMap(b => b.genres)));
      const allTropes = Array.from(new Set(atBooks.flatMap(b => b.tropes)));
      const allWarnings = Array.from(new Set(atBooks.flatMap(b => b.contentWarnings.flatMap(w => w.items))));
      series = {
        id: atSeries.id as any,
        name: atSeries.name,
        author: atSeries.authorName,
        authorId: atSeries.authorSlug,
        authorAvatar: atBooks[0]?.cover || seriesData.authorAvatar,
        authorBio: 'AfterTide author bio coming soon.',
        description: atSeries.description,
        averageRating: atBooks.length ? Number((atBooks.reduce((sum, b) => sum + b.rating, 0) / atBooks.length).toFixed(1)) : 0,
        totalReviews: atBooks.reduce((sum, b) => sum + b.reviewCount, 0),
        averageSpice: avgSpice,
        averageDarkness: avgDarkness,
        releasedCount: atBooks.length,
        totalBooks: atBooks.length,
        status: 'Complete',
        nextBookDate: '',
        genres: allGenres,
        tropes: allTropes,
        themes: [],
        contentWarnings: allWarnings,
        sharedTags: [],
        bundleFormats: seriesData.bundleFormats,
        retailers: seriesData.retailers,
        narrators: [],
        donationInfo: {
          hasDonation: false,
          percentage: 0,
          nonprofitName: '',
          nonprofitSlug: ''
        },
        books: atBooks.map((b, i) => ({
          id: b.id as any,
          title: b.title,
          position: b.series?.number || i + 1,
          coverImage: b.cover,
          rating: b.rating,
          spiceLevel: b.spiceLevel || 0,
          darknessLevel: b.darknessTier,
          pages: b.pages,
          releaseDate: new Date(b.publishDate).getFullYear().toString(),
          description: b.blurb,
          isPreorder: false
        })),
        reviews: []
      };
    }
  }
  const [modalBookId, setModalBookId] = useState<number | null>(null);
  // Track reading status per book
  const [bookStatuses, setBookStatuses] = useState<Record<number, ReadStatus>>({
    101: 'read'
  });
  const [selectedFormat, setSelectedFormat] = useState(series.bundleFormats[0]);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const setBookStatus = (bookId: number, status: ReadStatus) => {
    setBookStatuses(prev => ({
      ...prev,
      [bookId]: status
    }));
  };
  const readCount = Object.values(bookStatuses).filter(s => s === 'read').length;
  const modalBook = series.books.find(b => b.id === modalBookId) ?? null;
  const spiceBadge = spiceLevelMap[Math.round(series.averageSpice)];
  const darknessBadge = darknessLevelMap[Math.round(series.averageDarkness)];
  const RetailerLinks = () => <div className={`pt-4 border-t ${dk.border}`} data-id="element-12883" data-mp="3pnjegf44qp7f">
      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-12884" data-mp="3ultbkv9ezzvo">
        Also available at
      </p>
      <div className="flex flex-wrap gap-2" data-id="element-12885" data-mp="3c4cu46ore38">
        {series.retailers.map(retailer => <a key={retailer.name} href={retailer.url} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${dk.card} ${dk.border} ${dk.textSecondary} ${dk.isDark ? 'hover:border-gray-500 hover:text-white' : 'hover:border-gray-400 hover:text-gray-900'}`} data-id="element-12886" data-mp="31u1a13xxeob3">
            {retailer.name}
          </a>)}
      </div>
    </div>;
  return <div className={`min-h-screen pb-20 ${dk.page}`} data-id="element-12887" data-mp="2o4ppm5j8lokd">
      {/* --- BOOK DETAIL MODAL (flex-centered, matches BookDetailModal pattern) --- */}
      <AnimatePresence data-id="element-12888" data-mp="2gk5c2bddig3o">
        {modalBook && <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" data-id="element-12889" data-mp="2giu5k3sjmu8v">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setModalBookId(null)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-12890" data-mp="26m7olq3c8nc4" />

            {/* Panel */}
            <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 40
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 300
        }} className={`relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl border overflow-hidden ${dk.card} ${dk.border}`} style={{
          maxHeight: '90vh'
        }} data-id="element-12891" data-mp="1nvzqxnieqp3b">
              {/* Drag handle (mobile) */}
              <div className="flex justify-center pt-3 pb-1 sm:hidden" data-id="element-12892" data-mp="vb2o8w0j0edb">
                <div className={`w-10 h-1 rounded-full ${dk.isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-12893" data-mp="1izvpn7zx3efb" />
              </div>

              {/* Header row: cover + title + close */}
              <div className={`flex items-start gap-4 px-5 pt-4 pb-4 border-b ${dk.border}`} data-id="element-12894" data-mp="19ac1pnxckudl">
                <div className="flex-shrink-0 w-16 rounded-lg overflow-hidden shadow-md" data-id="element-12895" data-mp="1e7m03t2psgdk">
                  <img src={modalBook.coverImage} alt={modalBook.title} className="w-full aspect-[2/3] object-cover" data-id="element-12896" data-mp="21xgb053h3uv6" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5" data-id="element-12897" data-mp="2g2gpk6h6cnn8">
                  <div className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${dk.textMuted}`} data-id="element-12898" data-mp="2c2y6lg8wymn1">
                    Book {modalBook.position} of {series.totalBooks}
                  </div>
                  <h3 className={`font-bold text-lg leading-tight line-clamp-2 ${dk.textHeading}`} data-id="element-12899" data-mp="2kv9uovgk1xzu">
                    {modalBook.title}
                  </h3>
                  <p className={`text-sm mt-0.5 ${dk.textSecondary}`} data-id="element-12900" data-mp="1t550k52ztwlf">
                    by {series.author}
                  </p>
                  {/* Rating + release date inline */}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-id="element-12901" data-mp="223h04wlu84q0">
                    {modalBook.rating > 0 && <div className="flex items-center gap-1" data-id="element-12902" data-mp="3ignqkyucaqrf">
                        <StarIcon className="w-3.5 h-3.5 text-amber-400 fill-current" data-id="element-12903" data-mp="3dicf6joec873" />
                        <span className={`text-xs font-medium ${dk.textPrimary}`} data-id="element-12904" data-mp="1n33gc4azwpwn">
                          {modalBook.rating}
                        </span>
                      </div>}
                    <span className={`text-xs ${dk.textMuted}`} data-id="element-12905" data-mp="1x2uau5zk34wq">
                      {modalBook.releaseDate}
                    </span>
                    {modalBook.pages > 0 && <span className={`text-xs ${dk.textMuted}`} data-id="element-12906" data-mp="25z1vzlmvkvms">
                        {modalBook.pages} pages
                      </span>}
                  </div>
                  {/* Spice + darkness */}
                  <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-12907" data-mp="2kpsokvwajyqy">
                    {modalBook.spiceLevel > 0 && spiceLevelMap[modalBook.spiceLevel] && <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${rt.badgeRadius} text-xs font-medium ${darkMode ? `${spiceLevelMap[modalBook.spiceLevel].darkBg} ${spiceLevelMap[modalBook.spiceLevel].darkText}` : `${spiceLevelMap[modalBook.spiceLevel].bg} ${spiceLevelMap[modalBook.spiceLevel].text}`}`} data-id="element-12908" data-mp="2opt2mffha7xh">
                          <FlameIcon className={`w-3 h-3 ${spiceLevelMap[modalBook.spiceLevel].iconColor}`} fill="currentColor" data-id="element-12909" data-mp="92yxvd8fpl7h" />
                          {spiceLevelMap[modalBook.spiceLevel].label}
                        </span>}
                    {modalBook.darknessLevel > 0 && darknessLevelMap[modalBook.darknessLevel] && <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${rt.badgeRadius} text-xs font-medium ${darkMode ? `${darknessLevelMap[modalBook.darknessLevel].darkBg} ${darknessLevelMap[modalBook.darknessLevel].darkText}` : `${darknessLevelMap[modalBook.darknessLevel].bg} ${darknessLevelMap[modalBook.darknessLevel].text}`}`} data-id="element-12910" data-mp="n39piihkj3tp">
                          <MoonIcon className={`w-3 h-3 ${darknessLevelMap[modalBook.darknessLevel].iconColor}`} fill="currentColor" data-id="element-12911" data-mp="23mqr6oqczmqd" />
                          {darknessLevelMap[modalBook.darknessLevel].label}
                        </span>}
                  </div>
                </div>
                <button onClick={() => setModalBookId(null)} className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${dk.textMuted} ${dk.dropdownItemHover}`} data-id="element-12912" data-mp="1kwiscitozifz">
                  <XIcon className="w-5 h-5" data-id="element-12913" data-mp="1ayutphsytqsj" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto px-5 py-4 space-y-4" style={{
            maxHeight: 'calc(90vh - 220px)'
          }} data-id="element-12914" data-mp="31b0rmzahy2fd">
                {/* Description */}
                <p className={`text-sm leading-relaxed ${dk.textSecondary}`} data-id="element-12915" data-mp="pmw11rwbf1a2">
                  {modalBook.description}
                </p>

                {/* Genre tags */}
                {series.genres.length > 0 && <div data-id="element-12916" data-mp="ynbz2luahg7j">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${dk.textMuted}`} data-id="element-12917" data-mp="ymsjvcw0x5x7">
                      Genres
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-id="element-12918" data-mp="18cvjhvxnhi4i">
                      {series.genres.map((g, i) => <Link key={i} to={isAfterTide ? `/aftertide/browse?genre=${encodeURIComponent(g)}` : `/browse?genre=${encodeURIComponent(g)}`} className={`px-2.5 py-1 ${rt.badgeRadius} text-xs font-medium transition-colors ${dk.isDark ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`} data-id="element-12919" data-mp="1mcda8guib5gi" data-mpi={i}>
                          {g}
                        </Link>)}
                    </div>
                  </div>}

                {/* Tropes */}
                {series.tropes.length > 0 && <div data-id="element-12920" data-mp="2fczaorzudwyr">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${dk.textMuted}`} data-id="element-12921" data-mp="2jc857z2phyvd">
                      Tropes
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-id="element-12922" data-mp="2tae9uofwdj3x">
                      {series.tropes.slice(0, 5).map((t, i) => <Link key={i} to={isAfterTide ? `/aftertide/tagged-books?tag=${encodeURIComponent(t)}&category=tropes` : `/tagged-books?tag=${encodeURIComponent(t)}&category=tropes`} className={`px-2.5 py-1 ${rt.badgeRadius} text-xs font-medium transition-colors ${dk.isDark ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'}`} data-id="element-12923" data-mp="3kam6r5rqjc5" data-mpi={i}>
                          {t}
                        </Link>)}
                    </div>
                  </div>}

                {/* Content warnings */}
                {series.contentWarnings.length > 0 && <div data-id="element-12924" data-mp="veqnpga6fte7">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${dk.textMuted}`} data-id="element-12925" data-mp="vfii4ns1fl2w">
                      Content Warnings
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-id="element-12926" data-mp="2ptt7cxcjoh97">
                      {series.contentWarnings.map((w, i) => <span key={i} className={`px-2.5 py-1 ${rt.badgeRadius} text-xs font-medium ${dk.isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-800'}`} data-id="element-12927" data-mp="3j2jhothsbamq" data-mpi={i}>
                          {w}
                        </span>)}
                    </div>
                  </div>}

                {/* Reading Status */}
                <div data-id="element-12928" data-mp="2vd7b81dudgo4">
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${dk.textMuted}`} data-id="element-12929" data-mp="2vcp4gatjzk1y">
                    Reading Status
                  </p>
                  <div className="grid grid-cols-2 gap-2" data-id="element-12930" data-mp="2me3psqr7cp6g">
                    {STATUS_OPTIONS.map(opt => {
                  const current = bookStatuses[modalBook.id];
                  const isActive = current === opt.value;
                  return <button key={opt.value} onClick={() => setBookStatus(modalBook.id, opt.value)} className={`px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors border ${isActive ? `${rt.bgDark} border-transparent text-white` : `${dk.card} ${dk.border} ${dk.textSecondary} ${dk.isDark ? 'hover:border-gray-500' : 'hover:border-gray-400'}`}`} data-id="element-12931" data-mp="22jk775gsufr4">
                          {opt.label}
                        </button>;
                })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`flex gap-3 px-5 py-4 border-t ${dk.border}`} data-id="element-12932" data-mp="1snh7e69f6t7c">
                <Link to={isAfterTide ? `/aftertide/book/${modalBook.id}` : `/book/${modalBook.id}`} className={`flex-1 py-2.5 text-center rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${rt.secondaryButton}`} data-id="element-12933" data-mp="1xlit6yy8d6mi">
                  <BookOpenIcon className="w-4 h-4" data-id="element-12934" data-mp="14ui1u5ul33r9" /> View Book Page
                </Link>
                <button className={`flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${rt.primaryButton}`} data-id="element-12935" data-mp="1ivkl6au71zyh">
                  <ShoppingCartIcon className="w-4 h-4" data-id="element-12936" data-mp="q6dntf6h7get" />
                  {(modalBook as any).isPreorder ? 'Pre-Order' : 'Buy Now'}
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* --- HERO SECTION --- */}
      <div className="relative overflow-hidden" data-id="element-12937" data-mp="v5qds8sn93lm">
        <div className="absolute inset-0 z-0" data-id="element-12938" data-mp="v4ygmtjeext8">
          <div className={`absolute inset-0 ${dk.page} opacity-90 z-10`} data-id="element-12939" data-mp="7g5qjdpgjk6j" />
          <div className={`absolute inset-0 bg-gradient-to-b ${dk.isDark ? 'from-gray-900/50 to-gray-900' : 'from-white/50 to-gray-50'} z-10`} data-id="element-12940" data-mp="7e2ry5a8oiq7" />
          <img src={series.books[0].coverImage} alt="" className="w-full h-full object-cover filter blur-3xl opacity-40 scale-110" data-id="element-12941" data-mp="2e3d2ujlr2bfm" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-12942" data-mp="254rfmb5c288i">
          <div className="mb-6" data-id="element-12943" data-mp="294jtn3dhx6l9">
            <BackButton data-id="element-12944" data-mp="295l3j4irzn1e" />
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-12945" data-mp="2wv5o1rukuscs">
            {/* Cover Stack */}
            <div className="relative flex-shrink-0 mx-auto md:mx-0 w-48 sm:w-56" data-id="element-12946" data-mp="31uikn4b93dsk">
              <div className="relative z-30 rounded-lg shadow-2xl" data-id="element-12947" data-mp="2nu7rtvqdrp4r">
                <img src={series.books[0].coverImage} alt={series.name} className="w-full rounded-lg object-cover aspect-[2/3]" data-id="element-12948" data-mp="2nty2mad5tm04" />
              </div>
              {series.books.length > 1 && <div className={`absolute top-2 left-2 w-full h-full rounded-lg border shadow-lg z-20 ${dk.card} ${dk.border}`} data-id="element-12949" data-mp="1hf6ubllpiyw2" />}
              {series.books.length > 2 && <div className={`absolute top-4 left-4 w-full h-full rounded-lg border shadow-lg z-10 ${dk.cardElevated}`} data-id="element-12950" data-mp="1rviw86o40t71" />}
              <div className={`absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 ${rt.badgeRadius} text-xs font-bold shadow-lg whitespace-nowrap z-40 flex items-center gap-1.5 ${series.status === 'Complete' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-amber-100 text-amber-800 border border-amber-200'}`} data-id="element-12951" data-mp="1ixf8d3pnjkc2">
                {series.status === 'Complete' ? <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-12952" data-mp="21m3pdel0l5ik" /> : <ClockIcon className="w-3.5 h-3.5" data-id="element-12953" data-mp="v7un2e610azm" />}
                {series.status} · {series.totalBooks} Books
              </div>
            </div>

            {/* Series Info */}
            <div className="flex-1 text-center md:text-left" data-id="element-12954" data-mp="31w4s7xj7ber9">
              <h1 className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-2 ${dk.textHeading}`} data-id="element-12955" data-mp="36u6baigmne2q">
                {series.name}
              </h1>

              <p className={`text-xl mb-3 ${dk.textSecondary}`} data-id="element-12956" data-mp="3fmgjju7yp9hc">
                by{' '}
                <Link to={isAfterTide ? `/aftertide/author/${series.authorId}` : `/author/${series.authorId}`} className={`transition-colors ${rt.accentLink}`} data-id="element-12957" data-mp="3fob8rkq9nnr8">
                  {series.author}
                </Link>
                {series.narrators && series.narrators.length > 0 && <>
                    {', '}
                    <span className={dk.textMuted} data-id="element-12958" data-mp="207j564n54oo5">Narrated by</span>{' '}
                    {series.narrators.map((narrator, idx) => <Fragment key={narrator.id} data-id="element-12959" data-mp="37hz0kxcyaxlo" data-mpi={idx}>
                        <Link to={`/narrator/${narrator.id}`} className={`transition-colors ${rt.accentLink}`} data-id="element-12960" data-mp="37iqw686jsvby">
                          {narrator.name}
                        </Link>
                        {idx < series.narrators.length - 1 && ', '}
                      </Fragment>)}
                  </>}
              </p>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-4" data-id="element-12961" data-mp="3li00oale6103">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${dk.card}`} data-id="element-12962" data-mp="418r28i5u2f8">
                  <StarIcon className="w-4 h-4 text-amber-400 fill-current" data-id="element-12963" data-mp="3v8l90pqvajw7" />
                  <span data-id="element-12964" data-mp="d2yi3d84qzch">{series.averageRating}</span>
                  <span className={dk.textMuted} data-id="element-12965" data-mp="833sre406htj">({series.totalReviews})</span>
                </div>
                {spiceBadge && <div className={`flex items-center gap-1.5 px-3 py-1.5 ${rt.badgeRadius} text-sm font-medium ${darkMode ? `${spiceBadge.darkBg} ${spiceBadge.darkText}` : `${spiceBadge.bg} ${spiceBadge.text}`}`} data-id="element-12966" data-mp="rpx771jilho1">
                    <FlameIcon className={`w-3.5 h-3.5 ${spiceBadge.iconColor}`} fill="currentColor" data-id="element-12967" data-mp="iqjt1vkg44wz" />
                    {spiceBadge.label}
                  </div>}
                {darknessBadge && <div className={`flex items-center gap-1.5 px-3 py-1.5 ${rt.badgeRadius} text-sm font-medium ${darkMode ? `${darknessBadge.darkBg} ${darknessBadge.darkText}` : `${darknessBadge.bg} ${darknessBadge.text}`}`} data-id="element-12968" data-mp="dr79bjovfdym">
                    <MoonIcon className={`w-3.5 h-3.5 ${darknessBadge.iconColor}`} fill="currentColor" data-id="element-12969" data-mp="3m5z5f3gaqqla" />
                    {darknessBadge.label}
                  </div>}
              </div>

              <div className={`flex flex-wrap gap-2 py-3 mb-4 border-t border-b ${dk.border}`} data-id="element-12970" data-mp="5h1ii06fyco8">
                {series.genres.map((genre, idx) => <Link key={idx} to={isAfterTide ? `/aftertide/browse?genre=${encodeURIComponent(genre)}` : `/browse?genre=${encodeURIComponent(genre)}`} className={`inline-block px-3 py-1 ${rt.badgeRadius} text-sm font-medium shadow-sm transition-all duration-200 ${dk.isDark ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`} data-id="element-12971" data-mp="3sm98dn4cm5ko" data-mpi={idx}>
                    {genre}
                  </Link>)}
                {series.tropes.slice(0, 3).map((trope, idx) => <Link key={idx} to={isAfterTide ? `/aftertide/tagged-books?tag=${encodeURIComponent(trope)}&category=tropes` : `/tagged-books?tag=${encodeURIComponent(trope)}&category=tropes`} className={`inline-block px-3 py-1 ${rt.badgeRadius} text-sm font-medium shadow-sm transition-all duration-200 ${dk.isDark ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'}`} data-id="element-12972" data-mp="1ffjbrcfde59r" data-mpi={idx}>
                    {trope}
                  </Link>)}
                {series.contentWarnings.length > 0 && <Link to={isAfterTide ? `/aftertide/content-warnings` : '/content-warnings'} className={`inline-flex items-center justify-center w-8 h-8 rounded-full shadow-sm transition-all duration-200 hover:scale-110 ${dk.isDark ? 'bg-amber-900/30 border border-amber-700 text-amber-400 hover:bg-amber-900/50' : 'bg-amber-50 border border-amber-200 text-amber-600 hover:bg-amber-100'}`} title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-12973" data-mp="1agpz1m7wqian">
                    <AlertTriangleIcon className="h-4 w-4" data-id="element-12974" data-mp="3jshcke866qph" />
                  </Link>}
              </div>

              <p className={`text-lg leading-relaxed max-w-3xl mb-6 ${dk.textSecondary}`} data-id="element-12975" data-mp="3531nr3s78vcf">
                {series.description}
              </p>

              <div className="mb-4" data-id="element-12976" data-mp="2w613n9c5xjes">
                <h3 className={`text-base font-medium mb-3 ${dk.textPrimary}`} data-id="element-12977" data-mp="3061harrftqks">
                  Bundle Formats
                </h3>
                <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-12978" data-mp="2m7bj18svtkuu">
                  {series.bundleFormats.map((format, index) => <button key={index} onClick={() => setSelectedFormat(format)} disabled={!format.available} className={`px-4 py-2 rounded-lg border flex items-center shadow-sm transition-all duration-200 ${selectedFormat.type === format.type ? `${rt.borderActive} ${rt.bgLight} ${rt.textPrimary}` : !format.available ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' : `${dk.card} ${dk.border} ${dk.textSecondary} ${dk.dropdownItemHover}`}`} data-id="element-12979" data-mp="2r4lgnbqfd2tr" data-mpi={index}>
                      {format.type === 'Audiobook' ? <HeadphonesIcon className="h-4 w-4 mr-2" data-id="element-12980" data-mp="3088zzfsyi6sq" /> : format.type === 'eBook' ? <BookOpenIcon className="h-4 w-4 mr-2" data-id="element-12981" data-mp="2v8mbubblb655" /> : <BookIcon className="h-4 w-4 mr-2" data-id="element-12982" data-mp="398nlog9u1nwr" />}
                      {format.type} —{' '}
                      {format.available ? `$${format.price.toFixed(2)}` : 'TBA'}
                    </button>)}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-5" data-id="element-12983" data-mp="3h9d52nmbubrl">
                <button className={`px-6 py-2.5 rounded-lg font-medium shadow-sm flex items-center gap-2 ${rt.primaryButton}`} data-id="element-12984" data-mp="3m868tobtaoek">
                  <ShoppingCartIcon className="w-5 h-5" data-id="element-12985" data-mp="3r1ixtzkekx7u" />
                  Buy Complete Series · ${selectedFormat.price.toFixed(2)}
                </button>
                <SaveToFavorites itemId={`series-${series.id}`} className={`!p-2.5 !rounded-lg border ${dk.card}`} data-id="element-12986" data-mp="3toyu8jyigr2" />
                <SaveToTracker productId={`series-${series.id}`} className={`!p-2.5 !rounded-lg border ${dk.card}`} data-id="element-12987" data-mp="dsmkn3pzzw6d" />
                <button className={`p-2.5 rounded-lg border ${dk.card} ${dk.textSecondary}`} data-id="element-12988" data-mp="2an99h5g5v8h0">
                  <Share2Icon className="w-5 h-5" data-id="element-12989" data-mp="wowk5seaz43z" />
                </button>
              </div>

              {/* Retailer Availability — desktop only */}
              <div className="hidden md:block" data-id="element-12990" data-mp="rx51e41e22oe">
                <RetailerLinks data-id="element-12991" data-mp="nwbdhany4nvi" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- DONATION STRIP --- */}
      {series.donationInfo?.hasDonation && <div className={`border-y ${dk.isDark ? 'bg-rose-900/10 border-rose-900/30' : 'bg-rose-50 border-rose-100'}`} data-id="element-12992" data-mp="1fnqwkuqansgz">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-2 text-sm" data-id="element-12993" data-mp="1bnqijb73xjag">
            <GiftIcon className={`w-4 h-4 ${dk.isDark ? 'text-rose-400' : 'text-rose-500'}`} data-id="element-12994" data-mp="16lhqch226l9e" />
            <span className={dk.isDark ? 'text-rose-200' : 'text-rose-800'} data-id="element-12995" data-mp="11qlypi0qhzwt">
              <span className="font-bold" data-id="element-12996" data-mp="3reuxxr0dupfr">
                {series.donationInfo.percentage}% of sales
              </span>{' '}
              go to support
              <Link to={`/donations/nonprofit/${series.donationInfo.nonprofitSlug}`} className="font-medium underline ml-1 hover:opacity-80" data-id="element-12997" data-mp="1e5ijbeu6bb21">
                {series.donationInfo.nonprofitName}
              </Link>
            </span>
          </div>
        </div>}

      {/* --- PROGRESS TRACKER --- */}
      <div className={`sticky top-0 z-40 border-b backdrop-blur-md ${dk.isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-white/80 border-gray-200'}`} data-id="element-12998" data-mp="1a1syn0gmddma">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3" data-id="element-12999" data-mp="21u1vw6k9a9zx">
          <div className="flex items-center justify-between" data-id="element-13000" data-mp="1nw3jv7teqpnl">
            <div className="flex items-center gap-3" data-id="element-13001" data-mp="1nxy1od9du1ng">
              <span className={`text-sm font-bold uppercase tracking-wider ${dk.textMuted}`} data-id="element-13002" data-mp="1stloim0e6ips">
                Your Progress
              </span>
              <div className="flex items-center gap-1" data-id="element-13003" data-mp="hg18klgc2yth">
                <span className={`text-lg font-bold ${rt.textPrimary}`} data-id="element-13004" data-mp="hdf10ltg0jb9">
                  {readCount}
                </span>
                <span className={dk.textMuted} data-id="element-13005" data-mp="4x5vb9dw2w8a">/</span>
                <span className={`text-lg font-medium ${dk.textSecondary}`} data-id="element-13006" data-mp="4wdwzqswqkeb">
                  {series.totalBooks}
                </span>
              </div>
            </div>
            <div className="hidden sm:flex flex-1 mx-8 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden" data-id="element-13007" data-mp="dyn7whs38fs8">
              <div className={`h-full ${rt.bgDark} transition-all duration-500`} style={{
              width: `${readCount / series.totalBooks * 100}%`
            }} data-id="element-13008" data-mp="3mdx5bcl18wto" />
            </div>
            <div className="text-xs font-medium text-green-600 dark:text-green-400" data-id="element-13009" data-mp="3rcgqvbvps6li">
              {readCount === series.totalBooks ? 'Series Complete! 🎉' : `${series.totalBooks - readCount} to go`}
            </div>
          </div>
        </div>
      </div>

      {/* --- NEXT RELEASE BANNER (mobile) --- */}
      {series.nextBookDate && <div className={`md:hidden border-b ${dk.isDark ? 'bg-amber-900/10 border-amber-900/30' : 'bg-amber-50 border-amber-100'}`} data-id="element-13010" data-mp="12ik9vhm1nzx4">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-sm" data-id="element-13011" data-mp="1ca7zre2yd8wh">
            <CalendarIcon className={`w-4 h-4 ${dk.isDark ? 'text-amber-400' : 'text-amber-500'}`} data-id="element-13012" data-mp="1h8j7nj9ot9c2" />
            <span className={dk.isDark ? 'text-amber-200' : 'text-amber-800'} data-id="element-13013" data-mp="jnn6g82x2mqa">
              Next Release:{' '}
              <span className="font-bold" data-id="element-13014" data-mp="omztotxp9vte">{series.nextBookDate}</span>
            </span>
          </div>
        </div>}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16" data-id="element-13015" data-mp="xlbs3i4t5xwe">
        {/* --- READING ORDER --- */}
        <section data-id="element-13016" data-mp="24zwzzp7u94x1">
          <div className="flex items-end justify-between mb-8" data-id="element-13017" data-mp="24ydhdrypmhs3">
            <div data-id="element-13018" data-mp="290gyox828gla">
              <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-13019" data-mp="28zyzpsu0jo9o">
                Reading Order
              </h2>
              <p className={dk.textSecondary} data-id="element-13020" data-mp="3thhrkmsy22qw">
                Follow the journey from beginning to end
              </p>
            </div>
            {series.nextBookDate && <div className={`hidden md:flex px-4 py-2 rounded-lg border items-center gap-2 text-sm ${dk.card}`} data-id="element-13021" data-mp="3kpo7iqe7i9wk">
                <CalendarIcon className="w-4 h-4 text-amber-500" data-id="element-13022" data-mp="3fpa63r741y7i" />
                <span className={dk.textSecondary} data-id="element-13023" data-mp="3at2x1pjf46lc">
                  Next Release:{' '}
                  <span className="font-medium" data-id="element-13024" data-mp="35s5gtihyxmmh">{series.nextBookDate}</span>
                </span>
              </div>}
          </div>

          {/* Grid — max-w-4xl so card width matches hero cover size (~200px) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl" data-id="element-13025" data-mp="2wuczy9wlm6wz">
            {series.books.map(book => {
            const status = bookStatuses[book.id];
            const isRead = status === 'read';
            const isReading = status === 'reading';
            return <div key={book.id} className="relative group w-full" data-id="element-13026" data-mp="3y5r8ueu24jp">
                  {/* Clickable cover — opens modal, scales on hover like UnifiedBookCard */}
                  <button onClick={() => setModalBookId(book.id)} className="block w-full relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" aria-label={`View details for ${book.title}`} data-id="element-13027" data-mp="3qaus3xuv8tw4">
                    {/* Cover image */}
                    <img src={book.coverImage} alt={book.title} className={`w-full h-full object-cover transition-all duration-300 ${isRead ? 'grayscale-[40%]' : ''}`} data-id="element-13028" data-mp="3cblf171yifwd" />

                    {/* Position badge — top-left inside cover */}
                    <div className={`absolute top-2 left-2 z-20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow border-2 transition-colors ${isRead ? 'bg-green-500 text-white border-green-400' : isReading ? 'bg-indigo-500 text-white border-indigo-400' : 'bg-black/60 border-white/40 text-white'}`} data-id="element-13029" data-mp="2v1hga0kwr1hn">
                      {isRead ? <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-13030" data-mp="3drqt04ebmbnl" /> : book.position}
                    </div>

                    {/* Pre-order badge — below position badge, top-left */}
                    {(book as any).isPreorder && <div className="absolute top-9 left-2 z-20 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded shadow-sm" data-id="element-13031" data-mp="3it6c3qwxu83c">
                        PRE-ORDER
                      </div>}

                    {/* Hover overlay — matches UnifiedBookCard exactly */}
                    <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover:opacity-100 flex flex-col p-3 transition-opacity duration-200 text-white z-20" data-id="element-13032" data-mp="2peoxsg0rwta2">
                      {/* Top: title + series position */}
                      <div className="min-w-0" data-id="element-13033" data-mp="2ud9nvmmb9wks">
                        <div className="flex items-center justify-between mb-0.5" data-id="element-13034" data-mp="2ucgr9eh2gngd">
                          <h3 className="font-bold text-white text-sm line-clamp-1" data-id="element-13035" data-mp="38bzjsyxe9q7k">
                            {book.title}
                          </h3>
                          <span className="ml-2 flex-shrink-0 text-[10px] bg-indigo-500/80 text-white px-1.5 py-0.5 rounded" data-id="element-13036" data-mp="33c3odo2rj73m">
                            {book.position}/{series.totalBooks}
                          </span>
                        </div>

                        {/* Author */}
                        <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-13037" data-mp="3r1660vxeczy4">
                          by {series.author}
                        </p>

                        {/* Series name */}
                        <div className="text-[11px] text-indigo-300 flex items-center mb-1.5" data-id="element-13038" data-mp="9j0mtdf2vmqm">
                          <BookOpenIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-13039" data-mp="ehk9xh5er4kk" />
                          <span className="line-clamp-1" data-id="element-13040" data-mp="jvpgaf95zg1">{series.name}</span>
                        </div>

                        {/* Spice & Darkness */}
                        <div className="flex flex-wrap gap-1 mt-1" data-id="element-13041" data-mp="3mz84cqclf7w7">
                          {book.spiceLevel > 0 && spiceLevelMap[book.spiceLevel] && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 ${rt.badgeRadius} font-medium ${spiceLevelMap[book.spiceLevel].darkBg} ${spiceLevelMap[book.spiceLevel].darkText}`} data-id="element-13042" data-mp="3dzbj6ypewr5w">
                                <FlameIcon className="h-2.5 w-2.5" data-id="element-13043" data-mp="38zyus9iw7c17" />
                                {spiceLevelMap[book.spiceLevel].label}
                              </span>}
                          {book.darknessLevel > 0 && darknessLevelMap[book.darknessLevel] && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 ${rt.badgeRadius} font-medium ${darknessLevelMap[book.darknessLevel].darkBg} ${darknessLevelMap[book.darknessLevel].darkText}`} data-id="element-13044" data-mp="15qn8dgp8v4a5">
                                <MoonIcon className="h-2.5 w-2.5" data-id="element-13045" data-mp="1tgr5r6bua7sp" />
                                {darknessLevelMap[book.darknessLevel].label}
                              </span>}
                        </div>

                        {/* Content warnings icon */}
                        {series.contentWarnings.length > 0 && <div className="flex items-center mt-1.5" data-id="element-13046" data-mp="d0empevi21qt">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-13047" data-mp="823d95949t94">
                              <AlertCircleIcon className="h-3 w-3" data-id="element-13048" data-mp="7wcgg7z9s2n5" />
                            </span>
                          </div>}
                      </div>

                      {/* Bottom: rating + release date inline */}
                      <div className="mt-auto flex items-center justify-between" data-id="element-13049" data-mp="lp256k66nlny">
                        <div className="flex items-center gap-1.5" data-id="element-13050" data-mp="qoezfpzdvv0k">
                          {book.rating > 0 && <div className="flex items-center gap-1" data-id="element-13051" data-mp="vo1e83uv1jbh">
                              <StarIcon className="h-4 w-4 text-amber-400 fill-current" data-id="element-13052" data-mp="vl5dk7rmogjc" />
                              <span className="text-[11px]" data-id="element-13053" data-mp="2shut6vqrppzr">{book.rating}</span>
                            </div>}
                          <span className="text-gray-400 text-[11px]" data-id="element-13054" data-mp="rq2mj3rbjc63">
                            {book.releaseDate}
                          </span>
                        </div>
                        {(book as any).isPreorder && <span className="text-[10px] text-amber-300 font-medium" data-id="element-13055" data-mp="10qqsrcah088f">
                            Pre-Order
                          </span>}
                      </div>
                    </div>
                  </button>
                </div>;
          })}
          </div>
        </section>

        {/* --- SERIES CONTENT CATEGORIES --- */}
        <section data-id="element-13056" data-mp="ht6cgtlruhzf">
          <h2 className={`text-2xl font-bold mb-6 ${dk.textHeading}`} data-id="element-13057" data-mp="htpm7n19q02d">
            Series Tags & Categories
          </h2>
          <div className={`rounded-lg border overflow-hidden ${dk.card} ${dk.border}`} data-id="element-13058" data-mp="czv6euosa58k">
            {[{
            key: 'genres',
            label: 'Genres',
            icon: <TagIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-13059" data-mp="1w0m88r9f7h4u" />,
            items: series.genres,
            linkBase: isAfterTide ? '/aftertide/tagged-books?tag=' : '/tagged-books?tag=',
            linkSuffix: '&category=genres',
            colorClass: dk.isDark ? 'bg-indigo-900/30 text-indigo-300 hover:bg-indigo-900/50' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }, {
            key: 'tropes',
            label: 'Tropes',
            icon: <TagIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-13060" data-mp="2jnlzvf2wjgum" />,
            items: series.tropes,
            linkBase: isAfterTide ? '/aftertide/tagged-books?tag=' : '/tagged-books?tag=',
            linkSuffix: '&category=tropes',
            colorClass: dk.isDark ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
          }, {
            key: 'themes',
            label: 'Themes',
            icon: <TagIcon className="h-5 w-5 text-emerald-600 mr-2" data-id="element-13061" data-mp="b6x7jeupgrjr" />,
            items: series.themes,
            linkBase: isAfterTide ? '/aftertide/tagged-books?tag=' : '/tagged-books?tag=',
            linkSuffix: '&category=themes',
            colorClass: dk.isDark ? 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          }, {
            key: 'warnings',
            label: 'Content Warnings',
            icon: <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-2" data-id="element-13062" data-mp="3eoynkbabt8l0" />,
            items: series.contentWarnings,
            linkBase: isAfterTide ? '/aftertide/tagged-books?tag=' : '/tagged-books?tag=',
            linkSuffix: '&category=contentWarnings',
            colorClass: dk.isDark ? 'bg-amber-900/30 text-amber-300 hover:bg-amber-900/50' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
          }].map((section, sIdx, arr) => <div key={section.key} className={sIdx < arr.length - 1 ? `border-b ${dk.border}` : ''} data-id="element-13063" data-mp="1z2uyne7v75lf" data-mpi={sIdx}>
                <button onClick={() => setExpandedSection(expandedSection === section.key ? null : section.key)} className="w-full px-6 py-4 flex items-center justify-between" data-id="element-13064" data-mp="16fh9w3eblgce">
                  <div className="flex items-center" data-id="element-13065" data-mp="2mu0mafk1r26a">
                    {section.icon}
                    <span className={`text-base font-medium ${dk.textPrimary}`} data-id="element-13066" data-mp="2qv2ewdjgkfza">
                      {section.label}
                    </span>
                  </div>
                  <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${expandedSection === section.key || expandedSection === 'all' ? 'rotate-180' : ''} ${dk.textMuted}`} data-id="element-13067" data-mp="3curvtpy5q98r" />
                </button>
                <div className={`transition-all duration-200 overflow-hidden ${expandedSection === section.key || expandedSection === 'all' ? 'max-h-96' : 'max-h-0'}`} data-id="element-13068" data-mp="3hvpa3rg1qj4z">
                  <div className="px-6 py-4 flex flex-wrap gap-2" data-id="element-13069" data-mp="3qn95q4pe0cvq">
                    {section.items.map((item, idx) => <Link key={idx} to={`${section.linkBase}${encodeURIComponent(item)}${section.linkSuffix}`} className={`inline-block px-3 py-1 rounded-full text-sm transition-colors ${section.colorClass}`} data-id="element-13070" data-mp="9fnklvspqmi0" data-mpi={idx}>
                        {item}
                      </Link>)}
                  </div>
                </div>
              </div>)}
            <div className={`px-6 py-4 border-t ${dk.border} ${dk.surfaceMuted}`} data-id="element-13071" data-mp="qprfgr6n5grg">
              <button onClick={() => setExpandedSection(expandedSection === 'all' ? null : 'all')} className={`w-full text-sm font-medium flex items-center justify-center transition-colors ${rt.accentLink}`} data-id="element-13072" data-mp="bzk2us3beryo">
                {expandedSection === 'all' ? 'Collapse All Categories' : 'View All Categories'}
              </button>
            </div>
          </div>

          {/* Retailer Availability — mobile only */}
          <div className="md:hidden mt-6" data-id="element-13073" data-mp="3u8ayd33fja9l">
            <RetailerLinks data-id="element-13074" data-mp="1nf8w2175wn7x" />
          </div>
        </section>

        {/* --- REVIEWS & AUTHOR --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-13075" data-mp="1sf3kly3gt0pt">
          <div className="lg:col-span-2" data-id="element-13076" data-mp="1schabqp6w73w">
            <h2 className={`text-2xl font-bold mb-6 ${dk.textHeading}`} data-id="element-13077" data-mp="2u7j3v6edd36q">
              Series Reviews
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-13078" data-mp="2z643p4pplkun">
              <div className={`p-6 rounded-xl border flex flex-col items-center justify-center text-center ${dk.card}`} data-id="element-13079" data-mp="344dxpbzp6feo">
                <div className="text-5xl font-bold mb-2 text-amber-500" data-id="element-13080" data-mp="38x75k8qgq3uz">
                  {series.averageRating}
                </div>
                <div className="flex text-amber-400 mb-2" data-id="element-13081" data-mp="3hvj1mqa3ltut">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-5 h-5 ${i < Math.floor(series.averageRating) ? 'fill-current' : 'text-gray-300'}`} data-id="element-13082" data-mp="3mw6ttfa2cgqm" data-mpi={i} />)}
                </div>
                <p className={dk.textMuted} data-id="element-13083" data-mp="1sj8n15nko281">
                  Based on {series.totalReviews} reviews across all books
                </p>
              </div>
              <div className="space-y-2" data-id="element-13084" data-mp="1pxreivxouzzb">
                {[5, 4, 3, 2, 1].map(star => <div key={star} className="flex items-center gap-3" data-id="element-13085" data-mp="1l5ps4h6t3svk">
                    <span className={`w-3 text-sm font-medium ${dk.textSecondary}`} data-id="element-13086" data-mp="1g6n0pzjr1l62">
                      {star}
                    </span>
                    <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden" data-id="element-13087" data-mp="1b5z0cn9mrrz6">
                      <div className="h-full bg-amber-400 rounded-full" style={{
                    width: star === 5 ? '70%' : star === 4 ? '20%' : '5%'
                  }} data-id="element-13088" data-mp="23whau9coovmm" />
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="space-y-4" data-id="element-13089" data-mp="njsfh0f3yhrj">
              {series.reviews.map(review => <div key={review.id} className={`p-6 rounded-xl border ${dk.card}`} data-id="element-13090" data-mp="ihl000v2kjkg">
                  <div className="flex justify-between items-start mb-2" data-id="element-13091" data-mp="3aqokt0ujlxvp">
                    <span className={`font-bold ${dk.textPrimary}`} data-id="element-13092" data-mp="3apvkahm6g4m0">
                      {review.user}
                    </span>
                    <div className="flex text-amber-400" data-id="element-13093" data-mp="31q08qbopm7lg">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'text-gray-300'}`} data-id="element-13094" data-mp="2n0kg0q5mbs34" data-mpi={i} />)}
                    </div>
                  </div>
                  <p className={dk.textSecondary} data-id="element-13095" data-mp="2dyl0k2a94w2a">"{review.text}"</p>
                </div>)}
              <button className={`w-full py-3 rounded-lg font-medium border ${dk.card} ${dk.textSecondary}`} data-id="element-13096" data-mp="24286h3asog70">
                Read All Reviews
              </button>
            </div>
          </div>

          <div data-id="element-13097" data-mp="1pere3yebyje1">
            <h2 className={`text-2xl font-bold mb-6 ${dk.textHeading}`} data-id="element-13098" data-mp="1pgaudozzkuft">
              About the Author
            </h2>
            <div className={`p-6 rounded-xl border ${dk.card}`} data-id="element-13099" data-mp="1lfqy0nn8luuq">
              <div className="flex items-center gap-4 mb-4" data-id="element-13100" data-mp="1zeqm9u6u87qd">
                <img src={series.authorAvatar} alt={series.author} className="w-16 h-16 rounded-full object-cover border-2 border-white dark:border-gray-700 shadow-md" data-id="element-13101" data-mp="1uem3fxigj0wz" />
                <div data-id="element-13102" data-mp="24bquaan8zkk4">
                  <h3 className={`font-bold text-lg ${dk.textHeading}`} data-id="element-13103" data-mp="24ca8phdovqte">
                    {series.author}
                  </h3>
                  <Link to={isAfterTide ? `/aftertide/author/${series.authorId}` : `/author/${series.authorId}`} className={`text-sm hover:underline ${rt.textPrimary}`} data-id="element-13104" data-mp="2d53u7wjqbmfp">
                    View Profile
                  </Link>
                </div>
              </div>
              <p className={`text-sm leading-relaxed mb-6 ${dk.textSecondary}`} data-id="element-13105" data-mp="pf7e22drv8hu">
                {series.authorBio}
              </p>
              <button className={`w-full py-2.5 rounded-lg font-medium ${rt.outlineButton}`} data-id="element-13106" data-mp="u7qqux4mxzll">
                Follow Author
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default SeriesDetailPage;